/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import { Coordinates } from '../../../components/RegistryWidgets/Common/MapUtils';
import { AddressResource, SearchQueryParams, AfterKeyPageQueryParams, PageQueryParams } from '../apiCommon.typings';
import { CashBoxesStatus, Claster } from '../cashBoxes/cashBoxesClient.typings';
import { CompanyDto, TaxationType } from '../companies/companiesClient.typings';
import { Viewport } from '../../../components/Map/typings';
import { CityResource } from '../shortLists/shortListsClient.typings';

//------MAP-------

export interface StoreOnMapResource {
    latitude: number;
    longitude: number;
    taxNumber: string;
}

export interface StorePoint {
    taxNumber: string;
    lat: number;
    lon: number;
}

export interface StoresForMap {
    afterKeyId?: string;
    clusters: Claster[];
    points: StorePoint[];
}

export interface StoresMapFilterParams extends SearchQueryParams {
    city: CityResource;
    storeTypes: StoreType[];
    afterKeyId?: string;
    limit?: number;
    storeStatuses: storeStatus[];
}

export interface StoresMapSearchParams {
    viewport: Viewport;
    filterParams: StoresMapFilterParams;
}

//--------------------

export interface StoreDetailsForMap {
    cashBoxesOnline: number;
    cashBoxesTotal: number;
    name: string;
    taxNumber: string;
    type: StoreType;
    url?: string;
}

interface StoresListItemDtoBase {
    taxNumber: string;
    name: string;
    fullAddress: string;
    storeType: StoreType;
    status: storeStatus;
    taxAuthority: string;
    taxationTypes: string[];
    mainActivity: string;
    middleActivity: string;
    lowerActivity: string;
}

export interface StoresListItemDto extends StoresListItemDtoBase {
    cashBoxes: Pick<StoreCashboxesStat, 'online' | 'offline'>;
    company: Pick<CompanyDto, 'taxNumber' | 'name'>;
}

export interface StoresListDto {
    afterKey?: string;
    stores: StoresListItemDto[];
}

export interface StoresListFilterParams extends Partial<SearchQueryParams> {
    taxationTypes?: TaxationType[];
    taxAuthorityIds?: number[];
    statuses?: storeStatus[];
    fromCashBoxesCount?: number;
    toCashBoxesCount?: number;
    inspectionStatuses?: InspectionStatus[];
    inspectedFromDateUtc?: string;
    inspectedToDateUtc?: string;
    inspectionResults?: InspectionResult[];
    regionCodes: string[];
    storeTypes?: StoreType[];
    mainActivities?: string[];
    middleActivities?: string[];
    lowerActivities?: string[];
}

export type StoresListRequestParams = StoresListFilterParams & AfterKeyPageQueryParams;
export type StoresListExcelRequestParams = StoresListFilterParams;

export const STORE_STATUSES = ['ACTIVE', 'SUSPENDED', 'CLOSED', 'ELIMINATED'] as const;

export type storeStatus = (typeof STORE_STATUSES)[number];

interface StoreCashboxesStat {
    count: number;
    online: number;
    offline: number;
}

export interface ServiceOperatorDto {
    name: string;
    taxNumber: string;
}

export interface StoreDetailsDto {
    taxNumber: string;
    name: string;
    fullAddress: string;
    address: AddressResource;
    status: storeStatus;
    taxAuthority: string;
    taxationTypes: string[];
    serviceOperators: ServiceOperatorDto[];
    companyTaxNumber: string;
    companyName?: string;
    lng?: number;
    lat?: number;
    storeType: StoreType;
    mainActivity?: string;
    middleActivity?: string;
    lowerActivity?: string;
}

export interface UpdatedStoreDetailsDto extends Coordinates {}

export interface StoreCashBoxesListFilterParams {
    status?: CashBoxesStatus[];
}

export type StoreCashBoxesListQueryParams = StoreCashBoxesListFilterParams & PageQueryParams;

export interface StoreCashbox {
    taxNumber: string;
    lastDocumentDateAtUtc: string;
    operatorTaxNumber: string;
    operatorName: string;
    online: boolean;
    validTime: boolean;
    status: CashBoxesStatus;
    cashRegisterModelName: string;
    storeType: StoreType;
}

export interface StoreCashboxesListDto {
    count: number;
    cashboxes: StoreCashbox[];
}

export interface StoreCoordinatesHistoryItemDto {
    lat: number;
    lng: number;
    insertedAtUtc: string;
    userName: string;
}

export interface StoreCoordinatesHistoryDto {
    count: number;
    items: StoreCoordinatesHistoryItemDto[];
}

export interface StoreInspectionDto {
    inspectionId: string;
    status: InspectionStatus;
    initiatorUserName: string;
    insertedAtUtc: string;
    initiatorComment: string;
    auditorName?: string;
    assignedAtUtc?: string;
    closedAtUtc?: string;
    productName?: string;
    inspectedAtUtc?: string;
    result?: InspectionResult;
    resultComment?: string;
    photos?: StorePhotoInfoDto[];
    isExpanded?: boolean;
}

export interface StoreInspectionsHistoryDto {
    count: number;
    items: StoreInspectionDto[];
}

export enum StoreType {
    AUTO_STATION = 'AUTO_STATION',
    CULTURAL = 'CULTURAL',
    TRANSPORTATION = 'TRANSPORTATION',
    MANUFACTURING = 'MANUFACTURING',
    PAYMENT_TERMINALS = 'PAYMENT_TERMINALS',
    EXCHANGE = 'EXCHANGE',
    GAMING = 'GAMING',
    SPORT = 'SPORT',
    INTRENET_CLUB = 'INTRENET_CLUB',
    EXHIBITIONS = 'EXHIBITIONS',
    TOURISM = 'TOURISM',
    COMMUNAL_SERVICES = 'COMMUNAL_SERVICES',
    OFFICES = 'OFFICES',
    PUBLISHING = 'PUBLISHING',
    WASTE_RECEPTION = 'WASTE_RECEPTION',
    PARKING = 'PARKING',
    CAR_SERVICE = 'CAR_SERVICE',
    COMPUTERS = 'COMPUTERS',
    CONSUMERS = 'CONSUMERS',
    LEGAL_SERVICES = 'LEGAL_SERVICES',
    HIGH_EDUCATION = 'HIGH_EDUCATION',
    TRAININGS = 'TRAININGS',
    SCHOOLS = 'SCHOOLS',
    PRESCHOOL = 'PRESCHOOL',
    GAS_STATION = 'GAS_STATION',
    CAFE = 'CAFE',
    CEREMONIAL_SERVICES = 'CEREMONIAL_SERVICES',
    CELEBRATIONS = 'CELEBRATIONS',
    PHARMACY = 'PHARMACY',
    MAGAZINES = 'MAGAZINES',
    MOBILE_TRADE = 'MOBILE_TRADE',
    WHOLESALE = 'WHOLESALE',
    HEALTH_RESORT = 'HEALTH_RESORT',
    HOSPITAL = 'HOSPITAL',
    HEALTH_SERVICES = 'HEALTH_SERVICES',
    MOTEL_RESTAURANT = 'MOTEL_RESTAURANT',
    MOTEL_CAMPING = 'MOTEL_CAMPING',
    HOSTEL = 'HOSTEL',
    BEAUTY = 'BEAUTY',
    TICKETS = 'TICKETS',
    QUARRY = 'QUARRY',
    ADVERTISING = 'ADVERTISING',
    COMMUNICATION = 'COMMUNICATION',
    BANK = 'BANK',
    PAWNSHOP = 'PAWNSHOP',
    INSURANCE = 'INSURANCE',
    BEACH = 'BEACH',
    ENTERTAINMENT = 'ENTERTAINMENT',
    RENT = 'RENT',
    PHOTOS = 'PHOTOS',
    TAXI = 'TAXI',
    TEAHOUSE = 'TEAHOUSE',
    NUTRITION = 'NUTRITION',
    RESTAURANTS = 'RESTAURANTS',
    STOCK = 'STOCK',
    MARKET = 'MARKET',
    RETAIL = 'RETAIL',
    DENTIST = 'DENTIST',
    HOTELS = 'HOTELS',
    PUBLIC_CATERING = 'PUBLIC_CATERING',
}

export enum StoreCategory {
    ADVERTISING = 'ADVERTISING',
    CATERING = 'CATERING',
    COMMUNICATION = 'COMMUNICATION',
    EDUCATION = 'EDUCATION',
    ENTERTAINMENT = 'ENTERTAINMENT',
    FINANCE = 'FINANCE',
    GAS_STATION = 'GAS_STATION',
    HEALTH_CARE = 'HEALTH_CARE',
    HOTELS = 'HOTELS',
    OTHER = 'OTHER',
    PRODUCTION = 'PRODUCTION',
    PUBLIC_SERVICES = 'PUBLIC_SERVICES',
    QUARRY = 'QUARRY',
    RENT = 'RENT',
    TICKETS = 'TICKETS',
    TRADE = 'TRADE',
}

export interface StorePhotoInfoDto {
    name: string;
    userName: string;
    insertedAtUtc: string;
    preview?: boolean;
    userId: number;
    url: string;
    presignedPutObjectUrl: string;
}

export interface SetStorePhotoOnPreviewDto {
    name: string;
    preview?: boolean;
    submitted?: boolean;
    bucketName: string;
}

export interface DeleteStorePhotoDto {
    name: string;
}

export enum S3Buckets {
    STORES_PHOTOS = 'photos-stores',
    INSPECTION_PHOTOS = 'photos-inspections',
}

export enum InspectionStatus {
    OPEN = 'OPEN',
    CLOSED = 'CLOSED',
    IN_PROGRESS = 'IN_PROGRESS',
}

export enum InspectionResult {
    RECEIPT_OK = 'RECEIPT_OK',
    NO_RECEIPT = 'NO_RECEIPT',
    STORE_CLOSED = 'STORE_CLOSED',
    STORE_NOT_FOUND = 'STORE_NOT_FOUND',
    CASHBOX_NOT_FOUND = 'CASHBOX_NOT_FOUND',
}
